import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { fictoanButtonMapping, type FictoanButtonSize } from "@/lib/fictoan-schema";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
    return (
        <nav
            role="navigation"
            aria-label="pagination"
            data-slot="pagination"
            className={cn("setu:mx-auto setu:flex setu:w-full setu:justify-center", className)}
            {...props}
        />
    );
}

function PaginationContent({ className, ...props }: React.ComponentProps<"ul">) {
    return (
        <ul
            data-slot="pagination-content"
            className={cn("setu:flex setu:flex-row setu:items-center setu:gap-1", className)}
            {...props}
        />
    );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
    return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
    isActive?: boolean;
    size?: React.ComponentProps<typeof Button>["size"];
} & React.ComponentProps<"a">;

function PaginationLink({ className, isActive, size = "icon", ...props }: PaginationLinkProps) {
    // Map Fictoan sizes to shadcn sizes
    const mappedSize = (
        size && fictoanButtonMapping.sizes[size as FictoanButtonSize]
            ? fictoanButtonMapping.sizes[size as FictoanButtonSize]
            : size
    ) as "icon" | "sm" | "default" | "lg" | "icon-sm" | "icon-lg";

    return (
        <a
            aria-current={isActive ? "page" : undefined}
            data-slot="pagination-link"
            data-active={isActive}
            className={cn(
                buttonVariants({
                    variant: isActive ? "outline" : "ghost",
                    size: mappedSize,
                }),
                className
            )}
            {...props}
        />
    );
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
    return (
        <PaginationLink
            aria-label="Go to previous page"
            size="default"
            className={cn("setu:gap-1 setu:px-2.5 setu:sm:pl-2.5", className)}
            {...props}
        >
            <ChevronLeftIcon />
            <span className="setu:hidden setu:sm:block">Previous</span>
        </PaginationLink>
    );
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
    return (
        <PaginationLink
            aria-label="Go to next page"
            size="default"
            className={cn("setu:gap-1 setu:px-2.5 setu:sm:pr-2.5", className)}
            {...props}
        >
            <span className="setu:hidden setu:sm:block">Next</span>
            <ChevronRightIcon />
        </PaginationLink>
    );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            aria-hidden
            data-slot="pagination-ellipsis"
            className={cn("setu:flex setu:size-9 setu:items-center setu:justify-center", className)}
            {...props}
        >
            <MoreHorizontalIcon className="setu:size-4" />
            <span className="setu:sr-only">More pages</span>
        </span>
    );
}

export {
    Pagination,
    PaginationContent,
    PaginationLink,
    PaginationItem,
    PaginationPrevious,
    PaginationNext,
    PaginationEllipsis,
};
